/**
 * Sidebar Navigation Component
 * Main navigation sidebar for the admin portal
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  Assignment as MissionsIcon,
  Bolt as BoltIcon,
  ReportProblem as ReportProblemIcon,
  People as PeopleIcon,
  VerifiedUser as VerifiedUserIcon,
  Description as DescriptionIcon,
  AccountBalance as FinanceIcon,
  School as TrainingIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Lock as LockIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Logout as LogoutIcon,
  ChevronRight as ChevronRightIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { AuthService } from '../../services/auth/AuthService';
import { canManageAdminUsers } from '../../utils/permissions';
import iconImage from '../../assets/icon.png';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
}


export const Sidebar: React.FC = () => {
  const theme = useTheme();
  const { mode, toggleTheme } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [currentUser, setCurrentUser] = useState<ReturnType<typeof AuthService.getCurrentUser>>(AuthService.getCurrentUser());

  // Listen for storage changes to update user info when login/logout happens
  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentUser(AuthService.getCurrentUser());
    };

    // Check user on mount and when storage changes
    window.addEventListener('storage', handleStorageChange);

    // Also check periodically (in case of same-tab changes)
    const interval = setInterval(() => {
      const user = AuthService.getCurrentUser();
      if (user?.id !== currentUser?.id || user?.role !== currentUser?.role) {
        setCurrentUser(user);
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentUser?.id, currentUser?.role]);

  // Build navigation items based on permissions
  const navItems = useMemo(() => {
    const baseItems: NavItem[] = [
      { label: 'Dashboard', path: '/', icon: <DashboardIcon /> },
      { label: 'Missions', path: '/missions', icon: <MissionsIcon /> },
      { label: 'Disputes', path: '/disputes', icon: <BoltIcon /> },
      { label: 'Complaints', path: '/complaints', icon: <ReportProblemIcon /> },
      { label: 'Users', path: '/users', icon: <PeopleIcon /> },
      { label: 'Ghost Verification', path: '/ghost-verification', icon: <VerifiedUserIcon /> },
      { label: 'Finance', path: '/finance', icon: <FinanceIcon /> },
      { label: 'Training', path: '/training', icon: <TrainingIcon /> },
      { label: 'Reports', path: '/reports', icon: <DescriptionIcon /> },
    ];

    // Check permissions (used for Audit Log and Settings)
    const canManage = canManageAdminUsers();

    // Add Audit Log as separate menu item only for admin users
    if (canManage) {
      baseItems.push({
        label: 'Audit Log',
        path: '/audit-log',
        icon: <HistoryIcon />
      });
    }

    // Build Settings menu based on permissions
    // Always check permissions fresh from storage, not from memoized currentUser
    const settingsChildren: NavItem[] = [];

    // Debug: log current user role
    if (currentUser) {
      console.log('Sidebar - Current user role:', currentUser.role, 'Can manage admin users:', canManage);
    }

    // Add Change Password for all authenticated users
    settingsChildren.push({
      label: 'Change Password',
      path: '/settings/change-password',
      icon: <LockIcon />
    });

    if (canManage) {
      settingsChildren.push({
        label: 'Admin Users',
        path: '/settings/admin-users',
        icon: <AdminPanelSettingsIcon />
      });
    }

    if (settingsChildren.length > 0) {
      baseItems.push({
        label: 'Settings',
        path: '/settings',
        icon: <SettingsIcon />,
        children: settingsChildren,
      });
    }

    return baseItems;
  }, [currentUser?.role, currentUser?.id]); // Use role and id as dependencies to ensure update on user change

  const handleNavigation = (path: string) => {
    // Check if trying to access admin-only Settings
    if (path.startsWith('/settings/admin-users') && !canManageAdminUsers()) {
      navigate('/', { replace: true });
      return;
    }
    navigate(path);
  };

  const handleToggleExpand = (path: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedItems(newExpanded);
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      // Immediately update current user state after logout
      setCurrentUser(null);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear user state and navigate to login even if logout API call fails
      setCurrentUser(null);
      navigate('/login', { replace: true });
    }
  };

  // Check if a path or any of its children is active
  const isItemActive = (item: NavItem): boolean => {
    if (location.pathname === item.path) {
      return true;
    }
    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }
    return false;
  };

  // Auto-expand Settings if on settings page
  useEffect(() => {
    if (location.pathname.startsWith('/settings')) {
      setExpandedItems(new Set(['/settings']));
    }
  }, [location.pathname]);

  return (
    <Box
      sx={{
        width: theme.layout.sidebar.width,
        height: '100vh',
        backgroundColor: theme.customColors.sidebarBackground,
        borderRight: `1px solid ${theme.customColors.tableBorder}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1000,
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          padding: theme.spacing(3),
          borderBottom: `1px solid ${theme.customColors.tableBorder}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
          }}
        >
          <Box
            component="img"
            src={iconImage}
            alt="MiSpy.ai Logo"
            sx={{
              width: 56,
              height: 56,
              borderRadius: theme.shapes.borderRadius.md,
              objectFit: 'contain',
            }}
          />
          <Box>
            <Box
              component="div"
              sx={{
                ...theme.typography.h6,
                color: theme.customColors.sidebarText,
                fontWeight: theme.typography.h6.fontWeight,
              }}
            >
              MiSpy.ai
            </Box>
            <Box
              component="div"
              sx={{
                ...theme.typography.caption,
                color: theme.customColors.sidebarTextSecondary,
              }}
            >
              Admin Panel
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Navigation Items */}
      <List
        sx={{
          flex: 1,
          padding: theme.spacing(1),
          overflowY: 'auto',
          // Custom Scrollbar
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.2)',
            },
          },
          // Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1) transparent'
            : 'rgba(0, 0, 0, 0.1) transparent',
        }}
      >
        {navItems.map((item) => {
          const isActive = isItemActive(item);
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = expandedItems.has(item.path);

          return (
            <React.Fragment key={item.path}>
              <ListItem disablePadding sx={{ marginBottom: theme.spacing(0.5) }}>
                <ListItemButton
                  onClick={(e) => {
                    if (hasChildren) {
                      handleToggleExpand(item.path, e);
                    } else {
                      handleNavigation(item.path);
                    }
                  }}
                  sx={{
                    borderRadius: theme.shapes.borderRadius.md,
                    backgroundColor: isActive ? theme.customColors.sidebarActive : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive
                        ? theme.customColors.sidebarActive
                        : theme.customColors.tableRowHover,
                    },
                    padding: theme.spacing(1.5, 2),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive
                        ? theme.palette.primary.main
                        : theme.customColors.sidebarTextSecondary,
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: isActive
                          ? theme.customColors.sidebarText
                          : theme.customColors.sidebarTextSecondary,
                        fontWeight: isActive ? 600 : 400,
                      },
                    }}
                  />
                  {hasChildren && (
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleExpand(item.path, e);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: isActive
                          ? theme.palette.primary.main
                          : theme.customColors.sidebarTextSecondary,
                        transition: 'transform 0.2s',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}
                    >
                      <ChevronRightIcon sx={{ fontSize: theme.typography.body1.fontSize }} />
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>
              {/* Render children if expanded */}
              {hasChildren && isExpanded && (
                <Box sx={{ paddingLeft: theme.spacing(3) }}>
                  {item.children!.map((child) => {
                    const isChildActive = location.pathname === child.path;
                    return (
                      <ListItem key={child.path} disablePadding sx={{ marginBottom: theme.spacing(0.5) }}>
                        <ListItemButton
                          onClick={() => handleNavigation(child.path)}
                          sx={{
                            borderRadius: theme.shapes.borderRadius.md,
                            backgroundColor: isChildActive ? theme.customColors.sidebarActive : 'transparent',
                            '&:hover': {
                              backgroundColor: isChildActive
                                ? theme.customColors.sidebarActive
                                : theme.customColors.tableRowHover,
                            },
                            padding: theme.spacing(1.5, 2),
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color: isChildActive
                                ? theme.palette.primary.main
                                : theme.customColors.sidebarTextSecondary,
                              minWidth: 40,
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            sx={{
                              '& .MuiListItemText-primary': {
                                color: isChildActive
                                  ? theme.customColors.sidebarText
                                  : theme.customColors.sidebarTextSecondary,
                                fontWeight: isChildActive ? theme.typography.subtitle2.fontWeight : theme.typography.body2.fontWeight,
                                fontSize: theme.typography.body2.fontSize,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Bottom Section - User Actions */}
      <Box
        sx={{
          borderTop: `1px solid ${theme.customColors.tableBorder}`,
          padding: theme.spacing(2),
        }}
      >
        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(2),
          }}
        >
          {/* Theme Toggle */}
          <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}>
            <IconButton
              onClick={toggleTheme}
              sx={{
                color: theme.customColors.sidebarTextSecondary,
                '&:hover': {
                  backgroundColor: theme.customColors.tableRowHover,
                },
              }}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Logout */}
          <Tooltip title="Logout">
            <IconButton
              onClick={handleLogout}
              sx={{
                color: theme.customColors.sidebarTextSecondary,
                '&:hover': {
                  backgroundColor: theme.customColors.tableRowHover,
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* User Profile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            padding: theme.spacing(1.5),
            borderRadius: theme.shapes.borderRadius.md,
            backgroundColor: theme.customColors.tableRowHover,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.customColors.sidebarActive,
            },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              backgroundColor: theme.palette.primary.main,
              fontSize: theme.typography.body2.fontSize,
            }}
          >
            {currentUser?.name?.charAt(0).toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box
              component="div"
              sx={{
                ...theme.typography.body2,
                color: theme.customColors.sidebarText,
                fontWeight: theme.typography.subtitle2.fontWeight,
              }}
            >
              {currentUser?.name || 'Admin User'}
            </Box>
            <Box
              component="div"
              sx={{
                ...theme.typography.caption,
                color: theme.customColors.sidebarTextSecondary,
              }}
            >
              {currentUser?.email || 'admin@mispy.ai'}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
